import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { backendService } from "../services/backend.service";
import type { ChatBotRequest, ChatbotMessage, ConversationMessage, UserGuideMessageRequest } from "../utils/models";
import { isHeadNurse } from "../utils/utilFunctions";
import { useFeatureFlag } from "./useOpenFeatureFlag";

export const useUserGuide = () => {
	return useQuery({
		queryKey: ["userGuide"],
		queryFn: async () => {
			const response = await backendService.getUserGuide();
			return response.data;
		},
	});
};

export const useSendUserGuideMessage = () =>
	useMutation({
		mutationFn: async ({ message, sessionId }: UserGuideMessageRequest): Promise<ChatbotMessage> => {
			const response = await backendService.sendUserGuideMessage(message, sessionId);
			return response.data;
		},
	});

export const useSendChatBotMessage = () =>
	useMutation({
		mutationFn: async ({ message, departmentId, sessionId }: ChatBotRequest): Promise<ChatbotMessage> => {
			const response = await backendService.sendChatBotMessage({ message, departmentId, sessionId });
			return response.data;
		},
	});
export const useChatbotConversations = () => {
	const { userInfo } = useAuth();
	const isUserGuideChatEnabled = useFeatureFlag(FEATURE_FLAGS.UserGuideChat);
	return useQuery({
		queryKey: ["chatbotConversations"],
		queryFn: async (): Promise<ConversationMessage[]> => {
			const response = await backendService.chatbotConversations();
			return response.data;
		},
		refetchOnWindowFocus: false,
		enabled: isHeadNurse(userInfo.role) && isUserGuideChatEnabled,
	});
};

export const useChatbotAllConversations = () => {
	const { userInfo } = useAuth();
	const isUserGuideChatEnabled = useFeatureFlag(FEATURE_FLAGS.UserGuideChat);

	return useQuery({
		queryKey: ["chatbotAllConversations"],
		queryFn: async (): Promise<ConversationMessage[]> => {
			const response = await backendService.chatbotAllConversations();
			return response.data;
		},
		refetchOnWindowFocus: false,
		enabled: isHeadNurse(userInfo.role) && userInfo.support && isUserGuideChatEnabled,
	});
};

export const useChatbotConversation = (sessionId: string) => {
	const { userInfo } = useAuth();
	const isUserGuideChatEnabled = useFeatureFlag(FEATURE_FLAGS.UserGuideChat);
	return useQuery({
		queryKey: ["chatbotConversation", sessionId],
		queryFn: async (): Promise<ConversationMessage[]> => {
			const response = await backendService.chatbotSessionConversations(sessionId);
			return response.data;
		},
		enabled: !!sessionId && isHeadNurse(userInfo.role) && isUserGuideChatEnabled,
		refetchOnWindowFocus: false,
	});
};

export const useDeleteConversation = () =>
	useMutation({
		mutationFn: async (sessionId: string): Promise<{ message: string }> => {
			const response = await backendService.deleteConversation(sessionId);
			return response.data;
		},
	});
